import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../config/URL";
import GlobalDelete from "../../components/common/GlobalDelete";

const YearlyPayrollReport = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    year: new Date().getFullYear().toString(),
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "",
        enableHiding: true,
        enableSorting: false,
        size: 20,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "centerName",
        header: "Centre",
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: "employeeName",
        header: "Employee",
        enableHiding: true,
        size: 120,
      },
      {
        accessorKey: "payrollMonth",
        header: "Payroll Month",
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: "grossPay",
        header: "Gross Pay",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => `$${cell.getValue().toFixed(2)}`,
      },
      {
        accessorKey: "bonus",
        header: "Bonus",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => `$${cell.getValue().toFixed(2)}`,
      },
      {
        accessorKey: "deductionAmount",
        header: "Deductions",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => `$${cell.getValue().toFixed(2)}`,
      },
      {
        accessorKey: "netPay",
        header: "Net Pay",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => `$${cell.getValue().toFixed(2)}`,
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => {
          const startDate = cell.getValue();
          if (!startDate) return "--";
          return new Date(startDate).toLocaleDateString("en-GB");
        },
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => {
          const endDate = cell.getValue();
          if (!endDate) return "--";
          return new Date(endDate).toLocaleDateString("en-GB");
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: "payrollType",
        header: "Payroll Type",
        enableHiding: true,
        size: 100,
      },
    ],
    []
  );

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: {
            backgroundColor: "#e0e0e0",
          },
          thumb: {
            color: "#eb862a",
          },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a",
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const getYearlyPayrollData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "") {
          queryParams.append(key, value);
        }
      });

      const response = await api.get(
        `/getYearlyPayrollReport${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`
      );

      // Add unique id to each row since API response doesn't include it
      const dataWithIds = response.data.map((item, index) => ({
        ...item,
        id: `row-${index}`, // Generate a simple unique ID
      }));

      setData(dataWithIds || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch yearly payroll report");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      centerId: selectedCenter,
      year: new Date().getFullYear().toString(),
    });
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);

  useEffect(() => {
    getYearlyPayrollData();
  }, [filters]);

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedId(null);
  };

  // Generate year options (last 5 years + current year)
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div className="container-fluid center my-4 px-2">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Report Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Yearly Payroll Report
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Yearly Payroll Report</strong>
          </span>
        </div>
        <div className="d-flex justify-content-between mb-3">
          <div className="d-lg-flex individual_fliters">
            <input type="hidden" name="centerId" value={filters.centerId} />
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="year"
                onChange={handleFilterChange}
                value={filters.year}
              >
                <option value="">Select Year</option>
                {yearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-2 ms-2">
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable
                columns={columns}
                data={data}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                initialState={{
                  pagination: { pageSize: 50, pageIndex: 0 },
                  columnVisibility: {},
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => navigate(`/payroll/view/${row.original.id}`),
                  style: { cursor: "pointer" },
                })}
              />
            </ThemeProvider>
            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <MenuItem>
                <GlobalDelete
                  path={`/deletePayroll/${selectedId}`}
                  onDeleteSuccess={getYearlyPayrollData}
                  onOpen={handleMenuClose}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default YearlyPayrollReport;